/**
 * ElecBid — Calculators Page
 * Browning Electric
 *
 * Houses all electrical calculator tools. Each calculator is shown as a card
 * that expands when tapped. First module: CEC 2024 Residential Load Calc.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadCalcWorkspace from "./LoadCalcWorkspace";
import VoltageDropCalc from "./VoltageDropCalc";

// ─── Calculator definitions ────────────────────────────────────────────────────
const CALCULATORS = [
  {
    key: "cec_load",
    title: "CEC Residential Load Calc",
    subtitle: "CEC 2024, Rule 8-200 — Basic demand for residential services",
    icon: "⚡",
    accentColor: "#3b82f6",
    available: true,
  },
  {
    key: "panel_schedule",
    title: "Panel Schedule Designer",
    subtitle: "CEC 2-100(3) circuit directory — /panel note or paste → sized PDF/PNG label",
    icon: "🔲",
    accentColor: "#0ea5e9",
    available: true,
    href: "/panel-schedule",
  },
  {
    key: "voltage_drop",
    title: "Voltage Drop Calculator",
    subtitle: "CEC 8-102 + Table D3 — cable & raceway, Cu/Al, 1φ/3φ/DC",
    icon: "📉",
    accentColor: "#8b5cf6",
    available: true,
  },
  {
    key: "conduit_fill",
    title: "Conduit Fill Calculator",
    subtitle: "CEC Table 8 — max fill for EMT, rigid, PVC conduit",
    icon: "🔧",
    accentColor: "#f59e0b",
    available: false,
  },
  {
    key: "wire_size",
    title: "Wire Size Selector",
    subtitle: "CEC Table 2 & 4 — ampacity by wire gauge and insulation type",
    icon: "🔌",
    accentColor: "#10b981",
    available: false,
  },
  {
    key: "transformer",
    title: "Transformer Sizing",
    subtitle: "kVA demand calculation for single & three-phase services",
    icon: "🔲",
    accentColor: "#ef4444",
    available: false,
  },
];

// ─── Styles ────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');

  .calc-page {
    font-family: 'IBM Plex Sans', system-ui, sans-serif;
    background: #0d0f12;
    min-height: 100vh;
    color: #e8eaf0;
    max-width: 480px;
    margin: 0 auto;
    padding-bottom: 32px;
  }

  /* ── Header */
  .calc-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 16px 14px;
    background: #161a20;
    border-bottom: 1px solid #2a3140;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  .calc-back {
    background: none;
    border: none;
    color: #8892a4;
    font-size: 18px;
    cursor: pointer;
    padding: 4px 8px;
    font-family: 'IBM Plex Sans', sans-serif;
  }
  .calc-header-info {
    flex: 1;
  }
  .calc-header-title {
    font-size: 17px;
    font-weight: 700;
    color: #f59e0b;
    letter-spacing: 0.01em;
  }
  .calc-header-sub {
    font-size: 12px;
    color: #8892a4;
    margin-top: 1px;
  }
  .calc-header-icon {
    font-size: 28px;
  }

  /* ── Card grid */
  .calc-list {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  /* ── Calculator card */
  .calc-card {
    background: #161a20;
    border: 1.5px solid #2a3140;
    border-radius: 12px;
    overflow: hidden;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .calc-card.expanded {
    border-color: #374152;
    box-shadow: 0 4px 24px rgba(0,0,0,0.3);
  }
  .calc-card.unavailable {
    opacity: 0.5;
  }

  .calc-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    transition: background 0.15s;
  }
  .calc-card-header:active {
    background: #1e2430;
  }
  .calc-card-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
  }
  .calc-card-info {
    flex: 1;
    min-width: 0;
  }
  .calc-card-title {
    font-size: 15px;
    font-weight: 700;
    color: #e8eaf0;
    margin-bottom: 2px;
  }
  .calc-card-sub {
    font-size: 11px;
    color: #8892a4;
    line-height: 1.4;
  }
  .calc-card-chevron {
    font-size: 14px;
    color: #555f72;
    transition: transform 0.2s;
    flex-shrink: 0;
  }
  .calc-card.expanded .calc-card-chevron {
    transform: rotate(180deg);
  }
  .calc-card-badge {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 3px 8px;
    border-radius: 4px;
    background: rgba(245,158,11,0.12);
    border: 1px solid rgba(245,158,11,0.25);
    color: #f59e0b;
    flex-shrink: 0;
  }

  /* ── Expanded body */
  .calc-card-body {
    padding: 0 16px 16px;
  }
  .calc-card-divider {
    height: 1px;
    background: #2a3140;
    margin-bottom: 16px;
  }

  /* ── Desktop */
  @media (min-width: 768px) {
    .calc-page {
      max-width: 640px;
    }
  }
`;

// ─── Component ─────────────────────────────────────────────────────────────────
export default function Calculators({ onBack }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState("cec_load"); // auto-expand first one
  const goBack = onBack || (() => navigate("/"));

  function toggleCard(key) {
    // Tools with their own page (the panel schedule grid needs more than this
    // 480/640px accordion and a deep-linkable URL) navigate instead of expanding.
    const calc = CALCULATORS.find(c => c.key === key);
    if (calc && calc.href) { navigate(calc.href); return; }
    setExpanded(prev => prev === key ? "" : key);
  }

  return (
    <>
      <style>{CSS}</style>
      <div className="calc-page">

        {/* Header */}
        <div className="calc-header">
          <button className="calc-back" onClick={goBack}>‹ Back</button>
          <div className="calc-header-info">
            <div className="calc-header-title">Electrical Calculators</div>
            <div className="calc-header-sub">Field reference tools — CEC 2024</div>
          </div>
          <div className="calc-header-icon">🔢</div>
        </div>

        {/* Calculator cards */}
        <div className="calc-list">
          {CALCULATORS.map(calc => {
            const isExpanded = expanded === calc.key;
            return (
              <div
                key={calc.key}
                className={`calc-card${isExpanded ? " expanded" : ""}${!calc.available ? " unavailable" : ""}`}
              >
                <div
                  className="calc-card-header"
                  onClick={() => calc.available && toggleCard(calc.key)}
                >
                  <div
                    className="calc-card-icon"
                    style={{
                      background: `${calc.accentColor}15`,
                      border: `1.5px solid ${calc.accentColor}30`,
                    }}
                  >
                    {calc.icon}
                  </div>
                  <div className="calc-card-info">
                    <div className="calc-card-title">{calc.title}</div>
                    <div className="calc-card-sub">{calc.subtitle}</div>
                  </div>
                  {calc.available ? (
                    <div className="calc-card-chevron">▼</div>
                  ) : (
                    <div className="calc-card-badge">Soon</div>
                  )}
                </div>

                {isExpanded && calc.available && (
                  <div className="calc-card-body">
                    <div className="calc-card-divider" />
                    {calc.key === "cec_load" && <LoadCalcWorkspace />}
                    {calc.key === "voltage_drop" && <VoltageDropCalc />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
